import { useContext } from "react";
import { hasCounterpartyDetailChanges } from "@common/helpers/counterpartyDetailChanges";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { AccrualDocumentImporterMode } from "./accrualDocumentImporter.lib";
import { AccrualImporterContext } from "./accrualImporter.context";
import { resolveAccrualRowExtraction } from "./accrualImporter.helpers";
import { useAccrualCounterpartyDetailDiff } from "./useAccrualCounterpartyDetailDiff";
import { accrualDraftCopy } from "./draftDetails/accrualDraftCopy";
import { AccrualDraftCounterpartyDetailChanges } from "./draftDetails/AccrualDraftCounterpartyDetailChanges";
import { useAccrualCounterpartyDetailChanges } from "./draftDetails/useAccrualCounterpartyDetailChanges";
import {
    useSaveAccrualDraftCustomer,
    useSaveAccrualDraftDetails,
} from "./draftDetails/useConfirmAccrualDraft";

export function useAccrualCounterpartyCellHasDetailChanges({
    financialDocument,
    mode,
    extractionId,
    counterpartyId,
}: {
    financialDocument: FinancialDocumentExtended;
    mode: AccrualDocumentImporterMode;
    extractionId?: string;
    counterpartyId: string | null;
}): boolean {
    const { changes, isError } = useAccrualCounterpartyDetailDiff({
        financialDocument,
        extractionId:
            resolveAccrualRowExtraction(financialDocument, mode, extractionId)
                ?.id ?? null,
        counterpartyId,
        mode,
    });
    return hasCounterpartyDetailChanges(changes) || isError;
}

export const AccrualCounterpartyCellDetailChangesButton: React.FC<{
    financialDocument: FinancialDocumentExtended;
    extractionId?: string;
    counterpartyId: string;
    isDisabled: boolean;
    onCreateNew: () => void;
}> = ({
    financialDocument,
    extractionId,
    counterpartyId,
    isDisabled,
    onCreateNew,
}) => {
    const { mode } = useContext(AccrualImporterContext);
    const resolvedExtractionId =
        resolveAccrualRowExtraction(financialDocument, mode, extractionId)
            ?.id ?? null;
    const saveDetails = useSaveAccrualDraftDetails({
        financialDocumentId: financialDocument.id,
        extractionId: resolvedExtractionId,
    });
    const saveCustomer = useSaveAccrualDraftCustomer(saveDetails);
    const detailChanges = useAccrualCounterpartyDetailChanges({
        financialDocument,
        extractionId: resolvedExtractionId,
        counterpartyId,
        mode,
        onCounterpartyLocalized: saveCustomer,
    });

    return (
        <AccrualDraftCounterpartyDetailChanges
            detailChanges={detailChanges}
            copy={accrualDraftCopy(mode)}
            isHighlighted={false}
            appearance="cell"
            isDisabled={isDisabled}
            onCreateNew={onCreateNew}
        />
    );
};
